import { useBrandingStore } from '../stores/brandingStore.js';
import {
  DEFAULT_TIME_ZONE,
  formatClockTime,
  formatDateWithPattern,
  partsInZone,
  safeTimeZone,
  ymdInZone,
} from '../lib/dates.js';

/**
 * Re-anchor an instant on the calendar day it falls on in `timeZone`, as a Date
 * parked at the DEVICE's midnight of that day.
 *
 * `formatDateWithPattern` reads its Y/M/D back with `getFullYear()` /
 * `getMonth()` / `getDate()` — the device's zone. Handing it a device-midnight
 * Date built from the COMPANY's calendar day makes those three getters return
 * exactly the company-zone day, whatever zone the reader is sitting in. Nothing
 * below midnight is kept, which is correct: the caller asked for a date.
 *
 * The alternative — `formatDateWithPattern(ymdInZone(tz, d), pattern)` — is
 * wrong in the same way the bug was: `new Date('2026-08-19')` parses as UTC
 * midnight and a reader west of Greenwich reads it back as the 18th.
 */
function anchorToCompanyDay(
  value: Date | string | null | undefined,
  timeZone: string
): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const [y, m, d] = partsInZone(timeZone, date);
  return new Date(y, m - 1, d);
}

/**
 * Returns a formatter bound to the tenant's configured `dateFormat`
 * (falls back to `DD-MM-YYYY`) AND to the tenant's timezone. Use this in page
 * components instead of `toLocaleDateString()` so the whole app stays consistent
 * with the Branding setting.
 *
 * The zone is not decoration. `formatDateWithPattern` reads Y/M/D off the
 * DEVICE's clock, so a punch stamped 2026-08-18T20:30:00Z rendered as a date
 * read `18-08-2026` in New York and `19-08-2026` in Dubai — the same fact,
 * two different days, on a product whose rollups are computed in one of them.
 * See docs/time-and-dates.md: the browser's zone may format a moment, it must
 * never decide which day that moment belongs to.
 */
export function useFormatDate(): (
  value: Date | string | null | undefined
) => string {
  const pattern = useBrandingStore((s) => s.branding?.dateFormat ?? 'DD-MM-YYYY');
  const timeZone = useCompanyTimeZone();
  return (value: Date | string | null | undefined) =>
    formatDateWithPattern(anchorToCompanyDay(value, timeZone), pattern);
}

/**
 * The tenant's configured IANA zone — the one the server's rollups, schedules
 * and reports are computed in (`general.timezone`, flagged "Affects pay").
 *
 * This is the answer to "which zone decides what day it is", and it is NOT the
 * browser's: a roaming admin, an HQ user abroad, or a laptop clock that
 * travelled with its owner all give a different day from the one the payroll
 * was computed in. See the header of lib/dates.ts.
 *
 * Falls back to the same `Asia/Dubai` the API's `safeTimeZone` uses, so a
 * bootstrap that has not landed yet still leaves client and server agreeing.
 */
export function useCompanyTimeZone(): string {
  const timezone = useBrandingStore((s) => s.branding?.timezone);
  return safeTimeZone(timezone, DEFAULT_TIME_ZONE);
}

/**
 * TODAY as `YYYY-MM-DD` in the company's zone — the value to compare a calendar
 * day against, to seed a date field with, and to cap a "no future dates" picker
 * at. Sample it ONCE per render and pass the string down, so every "today" and
 * "not yet" mark on a screen is measured against the same instant (§3.1).
 */
export function useCompanyToday(): string {
  const timeZone = useCompanyTimeZone();
  return ymdInZone(timeZone);
}

/**
 * Returns a formatter bound to the tenant's configured `timeFormat`
 * ('12h' / '24h', falling back to 24h) AND to the tenant's timezone. Use this
 * instead of a bare `toLocaleTimeString()` so times honour the General setting.
 *
 * THE ZONE IS THE LOAD-BEARING PART. This called `toLocaleTimeString([], …)`
 * with no `timeZone` — the browser's — twenty lines below `useCompanyTimeZone`,
 * whose own header says why that is wrong. 33 call sites render punch times
 * through it (my-attendance, corrections, the control room, site history), and
 * a held-punches panel that formats in the COMPANY zone shipped beside them, so
 * a roaming or overseas admin read two different clock times for one punch on
 * two screens. A punch is an instant; which wall clock it is shown against is a
 * company fact, not a property of the laptop reading it.
 *
 * Note this is a FORMAT, not a boundary: the instant is untouched. The zone is
 * here so every screen names the same instant the same way.
 */
export function useFormatTime(): (
  value: Date | string | number | null | undefined
) => string {
  const hour12 = useTenantHour12();
  const timeZone = useCompanyTimeZone();
  return (value) => formatClockTime(value, { timeZone, hour12 });
}

/**
 * The tenant's hour cycle as a boolean — `true` for 12-hour, and 24-hour for
 * everything else including an unresolved store.
 *
 * Exposed on its own for the two kinds of caller that cannot use the formatter
 * above: a component that hands the flag DOWN to a plain helper (the
 * corrections form's option list, the login page's lock-out sentence), and one
 * that needs the same look inside a string it builds itself. Both still get the
 * tenant's answer rather than the browser's, which is the whole point.
 */
export function useTenantHour12(): boolean {
  return useBrandingStore((s) => s.branding?.timeFormat) === '12h';
}

/**
 * Date AND time in one string, both in the tenant's own rendering — the
 * `dateFormat` pattern, then the clock through `formatClockTime`.
 *
 * For the stamps that need the day as well as the minute: a terminal's last
 * seen, a job's last run, an enrolled device's registration. Those all called
 * `toLocaleString()`, which is the browser's date order AND the browser's hour
 * cycle — the same drift as the bare `toLocaleTimeString()`, twice over.
 */
export function useFormatDateTime(): (
  value: Date | string | number | null | undefined
) => string {
  const formatDate = useFormatDate();
  const formatTime = useFormatTime();
  return (value) => {
    if (value === null || value === undefined || value === '') return '';
    const date = value instanceof Date ? value : new Date(value as string | number);
    if (Number.isNaN(date.getTime())) return '';
    const time = formatTime(date);
    return time ? `${formatDate(date)} ${time}` : formatDate(date);
  };
}
